import { StandardEvents } from '@shopify/events';

class KronoPackBuilder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[ref="addToCartButton"]');
    this.variantId = this.querySelector('[data-pack-variant-id]');
    this.presetProperty = this.querySelector('[data-pack-preset-property]');
    this.message = this.querySelector('[data-pack-message]');
    this.stickyBar = this.querySelector('[data-pack-sticky]');
    this.stickyButton = this.querySelector('[data-pack-sticky-submit]');
    this.galleryImage = this.querySelector('[data-gallery-main]');

    this.querySelectorAll('[data-pack-preset]').forEach((button) => {
      button.addEventListener('click', () => this.applyPreset(button));
    });
    this.querySelectorAll('input[name="krono-pack-size"], [data-color-code]').forEach((input) => {
      input.addEventListener('change', () => this.updateState());
    });
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event), { capture: true });
    this.stickyButton?.addEventListener('click', () => this.form?.requestSubmit());
    this.querySelectorAll('[data-gallery-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => this.selectGalleryImage(button));
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.addEventListener(StandardEvents.cartError, this.handleCartError);
    this.updateState();
    if (this.button && this.stickyBar && 'IntersectionObserver' in window) {
      this.submitObserver = new IntersectionObserver(([entry]) => {
        this.stickyBar.hidden = entry.isIntersecting;
      }, { threshold: 0.1 });
      this.submitObserver.observe(this.button);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate);
    document.removeEventListener(StandardEvents.cartError, this.handleCartError);
    this.submitObserver?.disconnect();
  }

  selectGalleryImage(button) {
    const image = button.querySelector('img');
    if (!this.galleryImage || !image) return;
    this.galleryImage.src = button.dataset.gallerySrc || image.currentSrc || image.src;
    this.galleryImage.removeAttribute('srcset');
    this.galleryImage.alt = image.alt;
    this.querySelectorAll('[data-gallery-thumbnail]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-current', String(active));
    });
  }

  applyPreset(button) {
    const colors = button.dataset.colors?.split(',') || [];
    colors.forEach((color, index) => {
      const option = this.querySelector(`[data-color-group="${index}"] [data-color-code="${color}"]`);
      if (option) option.checked = true;
    });
    this.setSelectedPreset(button.dataset.presetName || '');
    this.updateState(false);
  }

  setSelectedPreset(name) {
    this.querySelectorAll('[data-pack-preset]').forEach((button) => {
      const selected = button.dataset.presetName === name;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    if (this.presetProperty) this.presetProperty.value = name;
  }

  updateState(detectCustom = true) {
    const size = this.querySelector('input[name="krono-pack-size"]:checked');
    const colors = [...this.querySelectorAll('[data-color-group]')].map((group) =>
      group.querySelector('[data-color-code]:checked')
    );
    if (this.variantId) this.variantId.value = size?.dataset.variantId || '';

    if (detectCustom) {
      const codes = colors.map((input) => input?.dataset.colorCode).join(',');
      const matchingPreset = [...this.querySelectorAll('[data-pack-preset]')].find(
        (button) => button.dataset.colors === codes
      );
      this.setSelectedPreset(matchingPreset?.dataset.presetName || this.dataset.customPreset || '');
    }

    const sizeSummary = this.querySelector('[data-summary-size]');
    if (sizeSummary) sizeSummary.textContent = size?.value || '—';
    colors.forEach((input, index) => {
      const summary = this.querySelector(`[data-summary-color="${index}"]`);
      if (summary) summary.textContent = input?.value || '—';
    });
    const presetSummary = this.querySelector('[data-summary-preset]');
    if (presetSummary) presetSummary.textContent = this.presetProperty?.value || '—';

    const valid = Boolean(size && colors.every(Boolean));
    if (this.button) this.button.disabled = !valid;
    if (this.stickyButton) this.stickyButton.disabled = !valid;
    if (valid && this.message?.dataset.state === 'error') this.setMessage('', '');
  }

  handleSubmit(event) {
    this.updateState();
    if (!this.form?.checkValidity() || !this.variantId?.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setMessage(this.dataset.validationMessage || '', 'error');
      this.form?.reportValidity();
      return;
    }
    this.button?.setAttribute('aria-busy', 'true');
    this.setMessage(this.dataset.loadingMessage || '', 'loading');
  }

  handleCartUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target) || event.action !== 'add') return;
    event.promise?.then(({ detail }) => {
      this.button?.removeAttribute('aria-busy');
      if (detail?.didError) return;
      this.setMessage(this.dataset.successMessage || '', 'success');
    }).catch(() => this.button?.removeAttribute('aria-busy'));
  };

  handleCartError = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.button?.removeAttribute('aria-busy');
    this.setMessage(event.error || this.dataset.validationMessage || '', 'error');
  };

  setMessage(text, state) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.dataset.state = state;
  }
}

if (!customElements.get('krono-pack-builder')) customElements.define('krono-pack-builder', KronoPackBuilder);
